import React from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  TrendingUp, DollarSign, ChevronRight, ChevronLeft, ArrowRight, Award,
  AlertTriangle, CheckCircle2, Layers, Target, BarChart3,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import EngineWeakLinkCallout from '../engines/EngineWeakLinkCallout';
import EngineActionPlanCard from '../engines/EngineActionPlanCard';
import { WeakestMeasureTile } from '../health/HealthActionSpine';
import PeerBenchmarkTrend from '../common/PeerBenchmarkTrend';

// ── money + ROI band helpers (dark + light-remapped hex) ──
function fmtMoney(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}K`;
  return `$${Math.round(v)}`;
}
const roiBand = (m) => (m >= 5 ? 'Excellent' : m >= 3 ? 'Strong' : m >= 1 ? 'Positive' : 'Negative');
const barColor = (m) => (m >= 5 ? '#06b6d4' : m >= 3 ? '#14b8a6' : m >= 1 ? '#f59e0b' : '#f43f5e');
const barColorLight = (m) => (m >= 5 ? '#0e7490' : m >= 3 ? '#0f766e' : m >= 1 ? '#b45309' : '#be123c');
const scoreText = (m) => (m >= 5 ? 'text-cyan-300' : m >= 3 ? 'text-teal-300' : m >= 1 ? 'text-amber-300' : 'text-rose-300');
const bandChip = (m) => (
  m >= 5 ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
    : m >= 3 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
      : m >= 1 ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
        : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
);

export function DarkCard({ className = '', children, testId }) {
  return <div className={cn('rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl', className)} data-testid={testId}>{children}</div>;
}

// Gauge maps an ROI multiple onto a 0–5x arc (5x = full ring).
function RoiGauge({ multiple, size = 80 }) {
  const pct = Math.max(0, Math.min(100, ((multiple || 0) / 5) * 100));
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="42" fill="none" stroke="#1f2a44" strokeWidth="8" />
        <circle cx="50" cy="50" r="42" fill="none" stroke={barColor(multiple)} strokeWidth="8"
          strokeDasharray={`${pct * 2.64} 264`} strokeLinecap="round" />
      </svg>
      <div className={cn('absolute inset-0 flex items-center justify-center text-base font-bold', scoreText(multiple))}>{(multiple || 0).toFixed(1)}x</div>
    </div>
  );
}

function KpiTiles({ summary, weakest, navigate, perfValue }) {
  const avg = summary.avg_roi_multiple || 0;
  const pv = perfValue?.has_data ? perfValue.summary : null;
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-3 gap-4', pv ? 'xl:grid-cols-6' : 'xl:grid-cols-5')} data-testid="roi-kpi-tiles">
      <DarkCard className="p-5" testId="roi-kpi-avg">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Average ROI</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className={cn('text-4xl font-heading font-extrabold tabular-nums', scoreText(avg))}>{avg.toFixed(1)}</span>
              <span className="text-sm text-slate-500 font-semibold">x</span>
            </div>
            <span className={cn('mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', bandChip(avg))}>{roiBand(avg)}</span>
            <p className="text-xs text-slate-500 mt-2">{summary.scenario_count || 0} business case{summary.scenario_count === 1 ? '' : 's'}</p>
          </div>
          <RoiGauge multiple={avg} />
        </div>
      </DarkCard>

      <DarkCard className={cn("p-5", weakest && "relative overflow-hidden ring-1 ring-rose-500/40 mc-priority-glass shadow-lg shadow-rose-900/20")} testId="roi-kpi-priority">
        {weakest && <div className="absolute -top-6 -right-6 h-20 w-20 rounded-full bg-rose-500/10 blur-2xl" />}
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rose-300"><Target className="h-3.5 w-3.5 text-rose-400" /> Top Priority</div>
        <p className="mt-2 text-lg font-bold text-slate-100 leading-tight truncate" title={weakest?.name}>{weakest ? weakest.name : '—'}</p>
        {weakest && <p className="mt-2 text-sm text-rose-300 font-semibold tabular-nums">{(weakest.roi_multiple || 0).toFixed(2)}x <span className="text-slate-400 font-normal">lowest ROI</span></p>}
        {weakest && navigate && (
          <button onClick={() => navigate('/roi-health/priorities')} data-testid="roi-kpi-priority-action"
            className="mt-4 w-full justify-center inline-flex items-center gap-2 text-sm font-bold px-3 py-2.5 rounded-lg bg-rose-500 text-white mc-priority-cta hover:bg-rose-400 shadow-lg shadow-rose-500/30 transition-colors">
            Take Action <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </DarkCard>

      <DarkCard className="p-5" testId="roi-kpi-net-benefit">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"><TrendingUp className="h-3.5 w-3.5 text-emerald-400" /> Total Net Benefit</div>
        <p className="mt-2 text-3xl font-heading font-extrabold text-emerald-300 tabular-nums">{fmtMoney(summary.total_net_benefit)}</p>
        <p className="text-xs text-slate-400 mt-1">across all cases</p>
      </DarkCard>

      <DarkCard className="p-5" testId="roi-kpi-investment">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"><DollarSign className="h-3.5 w-3.5 text-[#44b6d2]" /> Total Investment</div>
        <p className="mt-2 text-3xl font-heading font-extrabold text-cyan-300 tabular-nums">{fmtMoney(summary.total_investment)}</p>
        <p className="text-xs text-slate-400 mt-1">learning spend</p>
      </DarkCard>

      <DarkCard className="p-5" testId="roi-kpi-best">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"><Award className="h-3.5 w-3.5 text-amber-400" /> Best Performing</div>
        <p className="mt-2 text-3xl font-heading font-extrabold text-amber-300 tabular-nums">{(summary.best_roi_multiple || 0)}x</p>
        <p className="text-xs text-slate-400 mt-1 truncate" title={summary.best_scenario}>{summary.best_scenario || '—'}</p>
      </DarkCard>

      {pv && (
        <DarkCard className="p-5 cursor-pointer hover:ring-emerald-500/40 transition-shadow" testId="roi-kpi-performance-value">
          <button type="button" onClick={() => navigate?.('/roi-health/performance-value')} className="text-left w-full" data-testid="roi-kpi-performance-value-link">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500"><Layers className="h-3.5 w-3.5 text-emerald-400" /> Performance Value</div>
            <p className="mt-2 text-3xl font-heading font-extrabold text-emerald-300 tabular-nums" data-testid="roi-kpi-performance-value-amount">{fmtMoney(pv.attributable)}</p>
            <p className="text-xs text-slate-400 mt-1 truncate">attributable KPI movement · {pv.priced_kpis}/{pv.total_kpis} KPIs priced{pv.at_risk ? ` · ${fmtMoney(pv.at_risk)} at risk` : ''}</p>
          </button>
        </DarkCard>
      )}
    </div>
  );
}

function ScenarioBreakdown({ scenarios, weakestId, onSelect }) {
  const rows = [...scenarios].filter((s) => typeof s.roi_multiple === 'number').sort((a, b) => b.roi_multiple - a.roi_multiple);
  const max = Math.max(1, ...rows.map((r) => r.roi_multiple || 0));
  return (
    <DarkCard className="p-5" testId="roi-scenario-breakdown">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Business Case Breakdown</div>
      <p className="text-xs text-slate-500 mt-1 mb-4">Click a case to see its investment, net benefit and coaching plan</p>
      <div className="space-y-1 max-h-[380px] overflow-y-auto pr-1">
        {rows.map((s) => {
          const c = barColor(s.roi_multiple);
          const isWeak = s.id === weakestId;
          return (
            <button key={s.id} onClick={() => onSelect(s.id)} className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.04] transition-colors text-left" data-testid={`roi-scenario-row-${s.id}`}>
              <span className="shrink-0 w-8 h-8 rounded-lg bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center">
                {s.is_best ? <Award className="h-4 w-4 text-amber-400" /> : <Layers className="h-4 w-4 text-[#44b6d2]" />}
              </span>
              <div className="min-w-0 w-36 sm:w-48 shrink-0">
                <p className="text-sm font-semibold text-slate-100 truncate">{s.name}</p>
                <p className="text-[11px] font-medium mc-band" style={{ color: c, '--bl': barColorLight(s.roi_multiple) }}>{roiBand(s.roi_multiple)} · {fmtMoney(s.net_benefit)} net</p>
              </div>
              <div className="flex-1 h-2 rounded-full bg-[#0b1220] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${((s.roi_multiple || 0) / max) * 100}%`, backgroundColor: c }} /></div>
              {isWeak && <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Constraint</span>}
              <span className="shrink-0 w-12 text-right text-lg font-bold tabular-nums mc-band" style={{ color: c, '--bl': barColorLight(s.roi_multiple) }}>{(s.roi_multiple || 0).toFixed(1)}x</span>
              <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-slate-300 transition-colors shrink-0" />
            </button>
          );
        })}
      </div>
    </DarkCard>
  );
}

function RoiTrend({ chartData }) {
  const data = (chartData || []);
  return (
    <DarkCard className="p-5" testId="roi-trend">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3"><BarChart3 className="h-4 w-4 text-[#44b6d2]" /> ROI Over Time</div>
      {data.length >= 2 ? (
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2a44" />
            <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} stroke="#1f2a44" />
            <YAxis yAxisId="money" tick={{ fill: '#94a3b8', fontSize: 10 }} stroke="#1f2a44" tickFormatter={(v) => fmtMoney(v)} />
            <YAxis yAxisId="roi" orientation="right" tick={{ fill: '#94a3b8', fontSize: 10 }} stroke="#1f2a44" tickFormatter={(v) => `${v}x`} />
            <RTooltip contentStyle={{ background: '#0b1220', border: '1px solid #22304d', borderRadius: 8, color: '#e2e8f0' }} />
            <Bar yAxisId="money" dataKey="Net Benefit" fill="#10b981" radius={[3, 3, 0, 0]} barSize={18} />
            <Line yAxisId="roi" type="monotone" dataKey="ROI Multiple" stroke="#44b6d2" strokeWidth={3} dot={{ r: 3, fill: '#44b6d2' }} activeDot={{ r: 5 }} />
          </ComposedChart>
        </ResponsiveContainer>
      ) : <p className="text-sm text-slate-500 py-10 text-center">Not enough history yet to plot a trend.</p>}
    </DarkCard>
  );
}

function AlertsStrip({ scenarios, weakest, navigate }) {
  const cells = [];
  if (weakest) cells.push({ label: weakest.name, sub: 'Lowest-ROI case', value: `${(weakest.roi_multiple || 0).toFixed(2)}x`, tone: 'text-rose-300' });
  [...scenarios].filter((s) => (s.net_benefit || 0) < 0).slice(0, 3).forEach((s) => cells.push({ label: s.name, sub: 'Negative net benefit', value: fmtMoney(s.net_benefit), tone: 'text-rose-300' }));
  const allClear = cells.length === 0;
  return (
    <DarkCard className="px-5 py-4" testId="roi-recent-alerts">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <span className={cn('w-8 h-8 rounded-lg ring-1 flex items-center justify-center', allClear ? 'bg-emerald-500/15 ring-emerald-500/30' : 'bg-rose-500/15 ring-rose-500/30')}>
            {allClear ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-400" />}
          </span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300">Financial Alerts</p>
            <p className="text-xs text-slate-500">{allClear ? 'Every case is returning above cost.' : 'Some cases are underperforming.'}</p>
          </div>
        </div>
        <button onClick={() => navigate('/scenarios')} className="text-xs font-semibold text-[#44b6d2] hover:text-cyan-300 inline-flex items-center gap-1 transition-colors" data-testid="roi-alerts-view-all">View all cases <ArrowRight className="h-3.5 w-3.5" /></button>
        <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-[260px]">
          {cells.slice(0, 4).map((c, i) => (
            <div key={i} className="rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] px-3 py-2" data-testid={`roi-alert-cell-${i}`}>
              <p className="text-[11px] font-semibold text-slate-200 truncate">{c.label}</p>
              <p className="text-[10px] text-slate-500 truncate">{c.sub}</p>
              <p className={cn('text-sm font-bold mt-0.5', c.tone)}>{c.value}</p>
            </div>
          ))}
        </div>
      </div>
    </DarkCard>
  );
}

function findWeakest(scenarios) {
  const ranked = [...scenarios].filter((s) => typeof s.roi_multiple === 'number').sort((a, b) => a.roi_multiple - b.roi_multiple);
  return ranked[0] || null;
}

export default function RoiLeanOverview({ summary, scenarios, chartData, selectedOrg, valueAtStake, perfValue, navigate }) {
  const weakest = findWeakest(scenarios);
  const financialImpact = buildFinancialImpact(weakest, summary, valueAtStake);
  const roiFocus = weakest ? {
    engine: 'roi',
    entityId: weakest.id,
    entityLabel: weakest.name || 'lowest-ROI case',
    pillarId: weakest.id,
    scenario_id: weakest.id,
    score: Math.round((weakest.roi_multiple || 0) * 100) / 100,
    scoreLabel: `${(weakest.roi_multiple || 0).toFixed(2)}x`,
    target: null,
    lift: 10,
    overallScore: summary.avg_roi_multiple,
    granularity: 'scenario',
    granularityLabel: 'business case',
    organizationId: selectedOrg && selectedOrg !== 'all' ? selectedOrg : undefined,
  } : null;
  return (
    <div className="space-y-4" data-testid="roi-overview">
      <KpiTiles summary={summary} weakest={weakest} navigate={navigate} perfValue={perfValue} />
      {roiFocus && <WeakestMeasureTile focus={roiFocus} onView={() => navigate(`/roi-health/deep-dives/${encodeURIComponent(weakest.id)}`)} testId="roi-weakest-measure-tile" />}
      <AlertsStrip scenarios={scenarios} weakest={weakest} navigate={navigate} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-7"><ScenarioBreakdown scenarios={scenarios} weakestId={weakest?.id} onSelect={(id) => navigate(`/roi-health/deep-dives/${encodeURIComponent(id)}`)} /></div>
        <div className="lg:col-span-5"><RoiTrend chartData={chartData} /></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <DarkCard className="p-5 lg:col-span-7" testId="roi-overview-action-plan">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Action Plan</div>
          {weakest ? (
            <div className="space-y-3">
              <EngineWeakLinkCallout
                entityLabel={weakest.name || 'lowest-ROI scenario'}
                score={weakest.roi_multiple}
                overall={summary.avg_roi_multiple}
                metricSuffix="x"
                description="is your lowest-ROI program right now."
                testIdPrefix="roi-weak-link"
                financialImpact={financialImpact}
              />
              <EngineActionPlanCard engine="roi" params={{ scenario_id: weakest.id, organization_id: selectedOrg, score: weakest.roi_multiple, overall_score: summary.avg_roi_multiple }} />
            </div>
          ) : <p className="text-sm text-slate-500">No priority scenario identified.</p>}
        </DarkCard>
        <DarkCard className="p-5 lg:col-span-5" testId="roi-peer">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Peer Benchmark</div>
          {typeof summary.peer_percentile === 'number' ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-baseline gap-2"><span className="text-3xl font-extrabold tabular-nums text-cyan-300">P{summary.peer_percentile}</span><span className="text-xs text-slate-400">percentile{summary.peer_benchmark?.industry_name ? ` · ${summary.peer_benchmark.industry_name}` : ''}</span></div>
              <p className="text-xs text-slate-400">You: <span className="font-semibold text-slate-200">{(summary.avg_roi_multiple || 0)}x</span>{summary.peer_median_roi ? <> · Peer median: <span className="font-semibold text-slate-200">{summary.peer_median_roi}x</span></> : null}</p>
              <PeerBenchmarkTrend stage="financial" organizationId={selectedOrg} testId="roi-benchmark-trend" />
            </div>
          ) : <p className="text-sm text-slate-500">No benchmark available.</p>}
        </DarkCard>
      </div>
    </div>
  );
}

function buildFinancialImpact(weakest, summary, valueAtStake) {
  if (!weakest) return undefined;
  const TARGET_MULTIPLE = 3;
  const LIFT_POINTS = 15;
  const realizedPct = Math.max(0, Math.min(100, ((weakest.roi_multiple || 0) / TARGET_MULTIPLE) * 100));
  const afterFixPct = Math.min(100, realizedPct + LIFT_POINTS);
  const deltaPct = afterFixPct - realizedPct;
  const vas = valueAtStake?.annual_value_at_stake_usd ?? null;
  const currency = valueAtStake?.currency || 'USD';
  return {
    realizedPct, afterFixPct, deltaPct, currency,
    liftLabel: `Lift ${weakest.name || 'scenario'} ROI +${LIFT_POINTS}pts`,
    dollarsLeaking: vas != null ? vas * (1 - realizedPct / 100) : null,
    dollarsUnlocked: vas != null ? vas * (deltaPct / 100) : null,
    leakCopy: `Every quarter this scenario runs at ${(weakest.roi_multiple || 0).toFixed(2)}x against a ${TARGET_MULTIPLE}x target, roughly ${Math.round(100 - realizedPct)}% of the potential ROI never lands.`,
    fixCopy: `Closing the gap by ${LIFT_POINTS}pts lifts realized ROI by ~${Math.round(deltaPct)} percentage points — pick the top 2-3 L&D measures tied to this scenario.`,
    vasFootnote: valueAtStake ? {
      revenue: valueAtStake.inputs?.annual_revenue,
      marginPct: valueAtStake.inputs?.operating_margin_pct,
      isDefault: !!valueAtStake.is_default,
    } : null,
  };
}

export function RoiDeepDive({ scenarios, summary, scenarioId, selectedOrg, navigate }) {
  const s = scenarios.find((x) => String(x.id) === String(scenarioId));
  const weakestId = findWeakest(scenarios)?.id;
  if (!s) return <div className="text-sm text-slate-400">Business case not found. <button className="text-[#44b6d2]" onClick={() => navigate('/roi-health/deep-dives')}>Back</button></div>;
  const c = barColor(s.roi_multiple);
  return (
    <div className="space-y-4" data-testid="roi-deep-dive">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => navigate('/roi-health/deep-dives')} className="text-xs text-slate-400 hover:text-slate-100 inline-flex items-center gap-1"><ChevronLeft className="h-3.5 w-3.5" /> All cases</button>
        <span className="text-slate-700">·</span>
        {[...scenarios].filter((x) => typeof x.roi_multiple === 'number').sort((a, b) => b.roi_multiple - a.roi_multiple).slice(0, 8).map((x) => (
          <button key={x.id} onClick={() => navigate(`/roi-health/deep-dives/${encodeURIComponent(x.id)}`)} className={cn('text-[11px] px-2.5 py-1 rounded-full transition-colors truncate max-w-[160px]', String(x.id) === String(scenarioId) ? 'bg-[#44b6d2] text-[#04222b] font-semibold' : 'text-slate-400 hover:bg-white/5')} data-testid={`roi-dd-switch-${x.id}`}>{x.name}</button>
        ))}
      </div>
      <DarkCard className="p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <span className="w-12 h-12 rounded-xl bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center shrink-0">{s.is_best ? <Award className="h-6 w-6 text-amber-400" /> : <Layers className="h-6 w-6 text-[#44b6d2]" />}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-100 truncate">{s.name}</h2>
              {s.id === weakestId && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Primary Constraint</span>}
              <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', bandChip(s.roi_multiple))}>{roiBand(s.roi_multiple)}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{s.analysis_type || 'Business case'}{s.date ? ` · ${s.date}` : ''}{s.lei_improvement != null ? ` · LEI +${s.lei_improvement}%` : ''}</p>
          </div>
          <RoiGauge multiple={s.roi_multiple} size={88} />
        </div>
      </DarkCard>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <DarkCard className="p-5" testId="roi-dd-investment">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Investment</div>
          <p className="mt-2 text-2xl font-heading font-extrabold text-cyan-300 tabular-nums">{fmtMoney(s.investment)}</p>
        </DarkCard>
        <DarkCard className="p-5" testId="roi-dd-impact">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Financial Impact</div>
          <p className="mt-2 text-2xl font-heading font-extrabold text-slate-100 tabular-nums">{fmtMoney(s.financial_impact)}</p>
        </DarkCard>
        <DarkCard className="p-5" testId="roi-dd-net-benefit">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Net Benefit</div>
          <p className={cn('mt-2 text-2xl font-heading font-extrabold tabular-nums', (s.net_benefit || 0) >= 0 ? 'text-emerald-300' : 'text-rose-300')}>{fmtMoney(s.net_benefit)}</p>
        </DarkCard>
      </div>
      {s.kpis_impacted?.length > 0 && (
        <DarkCard className="p-5" testId="roi-dd-kpis">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#44b6d2] mb-3">KPIs Impacted</div>
          <div className="flex gap-2 flex-wrap">
            {s.kpis_impacted.map((k, i) => (
              <span key={`${k}-${i}`} className="text-[11px] px-2.5 py-1 rounded-full bg-[#0b1220] ring-1 ring-[#22304d] text-slate-200">{k}</span>
            ))}
          </div>
        </DarkCard>
      )}
      <DarkCard className="p-5" testId="roi-dd-action-plan">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Action Plan</div>
        <EngineActionPlanCard engine="roi" params={{ scenario_id: s.id, organization_id: selectedOrg, score: s.roi_multiple, overall_score: summary.avg_roi_multiple }} />
      </DarkCard>
    </div>
  );
}
